import { useState, useEffect } from 'react';
import { supabase, Client } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useActivityLog } from '../../../hooks/useActivityLog';
import Button from '../../../components/base/Button';
import ClientModal from './ClientModal';
import ClientDetailModal from './ClientDetailModal';
import SendFormWhatsAppModal from '../../creators/components/SendFormWhatsAppModal';
import ExportCreatorsButton from '../../../components/feature/ExportCreatorsButton';

export default function ClientsSection() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [sendFormClient, setSendFormClient] = useState<Client | null>(null);
  const { user, hasPermission } = useAuth();
  const { logActivity } = useActivityLog();

  const canEdit = hasPermission('clients', 'edit');
  const canDelete = hasPermission('clients', 'delete');

  useEffect(() => {
    loadClients();
  }, []);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Erro ao carregar creators:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch =
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (client.phone || '').includes(searchTerm) ||
      (client.produtos_divulgados || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || client.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleAddClient = () => {
    setSelectedClient(null);
    setIsModalOpen(true);
  };

  const handleEditClient = (client: Client) => {
    setSelectedClient(client);
    setIsModalOpen(true);
  };

  const handleViewClient = (client: Client) => {
    setSelectedClient(client);
    setIsDetailOpen(true);
  };

  const handleSave = async (data: Record<string, unknown>) => {
    try {
      const payload = {
        ...data,
        updated_at: new Date().toISOString(),
      };

      if (selectedClient) {
        const { error } = await supabase.from('clients').update(payload).eq('id', selectedClient.id);
        if (error) throw error;
        
        await logActivity({
          action: 'update',
          module: 'creators',
          entityId: selectedClient.id,
          entityName: selectedClient.name,
          details: { before: selectedClient, after: { ...selectedClient, ...payload } }
        });
        
        showToast('Creator atualizado com sucesso!');
      } else {
        const { data: newClient, error } = await supabase.from('clients').insert([{ ...payload, created_by: user?.id }]).select().single();
        if (error) throw error;
        
        await logActivity({
          action: 'create',
          module: 'creators',
          entityId: newClient.id,
          entityName: (payload.name as string) || 'Novo creator',
          details: { data: payload }
        });
        
        showToast('Creator cadastrado com sucesso!');
      }
      await loadClients();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Erro ao salvar creator:', error);
      showToast('Erro ao salvar. Tente novamente.', 'error');
    }
  };

  const handleDeleteClient = async (id: string) => {
    try {
      const clientToDelete = clients.find(c => c.id === id);
      const { error } = await supabase.from('clients').delete().eq('id', id);
      if (error) throw error;
      
      if (clientToDelete) {
        await logActivity({
          action: 'delete',
          module: 'creators',
          entityId: id,
          entityName: clientToDelete.name,
          details: { deletedData: clientToDelete }
        });
      }
      
      await loadClients();
      setDeleteConfirm(null);
      showToast('Creator removido com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir creator:', error);
      showToast('Erro ao excluir. Tente novamente.', 'error');
    }
  };

  const handleSendForm = (client: Client) => {
    setSendFormClient(client);
  };

  const activeCount = clients.filter(c => c.status === 'active').length;
  const totalGmv = clients.reduce((sum, c) => sum + Number(c.gmv_geral || 0), 0);
  const totalVideos = clients.reduce((sum, c) => sum + Number(c.videos_30d || 0), 0);

  const formatCurrency = (val: number) =>
    `R$ ${val.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-3 border-[#5de0e6] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-[#5de0e6]/10 rounded-xl flex items-center justify-center">
            <i className="ri-user-star-line text-lg text-[#004aad]"></i>
          </div>
          <div>
            <p className="text-xl font-bold text-gray-900">{clients.length}</p>
            <p className="text-xs text-gray-400">Total de Creators</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
            <i className="ri-checkbox-circle-line text-lg text-emerald-600"></i>
          </div>
          <div>
            <p className="text-xl font-bold text-gray-900">{activeCount}</p>
            <p className="text-xs text-gray-400">Ativos</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
            <i className="ri-money-dollar-circle-line text-lg text-amber-600"></i>
          </div>
          <div>
            <p className="text-xl font-bold text-gray-900">{formatCurrency(totalGmv)}</p>
            <p className="text-xs text-gray-400">GMV Total</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-rose-50 rounded-xl flex items-center justify-center">
            <i className="ri-video-line text-lg text-rose-600"></i>
          </div>
          <div>
            <p className="text-xl font-bold text-gray-900">{totalVideos}</p>
            <p className="text-xs text-gray-400">Vídeos (30d)</p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="relative flex-1 w-full">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder="Buscar por nome, telefone ou produto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-gray-50/50"
            />
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              {[
                { value: 'all', label: 'Todos' },
                { value: 'active', label: 'Ativos' },
                { value: 'inactive', label: 'Inativos' }
              ].map((status) => (
                <button
                  key={status.value}
                  onClick={() => setFilterStatus(status.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap cursor-pointer transition-all ${
                    filterStatus === status.value
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>
            <ExportCreatorsButton clients={clients} filteredClients={filteredClients} />
            {canEdit && (
              <Button onClick={handleAddClient} size="md">
                <i className="ri-add-line text-sm"></i>
                <span className="hidden sm:inline">Novo Creator</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3.5 px-5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Creator</th>
                <th className="text-left py-3.5 px-5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">CPF/CNPJ</th>
                <th className="text-left py-3.5 px-5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">TikTok</th>
                <th className="text-left py-3.5 px-5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">GMV Geral</th>
                <th className="text-left py-3.5 px-5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Produtos</th>
                <th className="text-left py-3.5 px-5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Comissão</th>
                <th className="text-left py-3.5 px-5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Vídeos / Lives</th>
                <th className="text-left py-3.5 px-5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                <th className="text-left py-3.5 px-5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Amostra</th>
                {(canEdit || canDelete) && (
                  <th className="text-right py-3.5 px-5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Ações</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => {
                const tiktokCount = (client.tiktok_links || []).length;
                return (
                  <tr
                    key={client.id}
                    className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors group cursor-pointer"
                    onClick={() => handleViewClient(client)}
                  >
                    <td className="py-3.5 px-5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-gradient-to-br from-[#5de0e6] to-[#004aad] rounded-lg flex items-center justify-center shadow-sm">
                          <span className="text-white font-semibold text-xs">
                            {client.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{client.name}</p>
                          <p className="text-xs text-gray-400 truncate flex items-center gap-1">
                            <i className="ri-whatsapp-line text-emerald-500 text-[10px]"></i>
                            {client.phone}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3.5 px-5">
                      {client.cpf_cnpj ? (
                        <span className="text-sm text-gray-600 font-mono">{client.cpf_cnpj}</span>
                      ) : (
                        <span className="text-xs text-gray-300 italic">Não informado</span>
                      )}
                    </td>
                    <td className="py-3.5 px-5">
                      <div className="flex items-center gap-1.5">
                        <i className="ri-tiktok-line text-sm text-gray-800"></i>
                        <span className="text-sm text-gray-600">{tiktokCount} {tiktokCount === 1 ? 'conta' : 'contas'}</span>
                      </div>
                    </td>
                    <td className="py-3.5 px-5">
                      <span className="text-sm font-semibold text-gray-900">
                        {formatCurrency(Number(client.gmv_geral || 0))}
                      </span>
                    </td>
                    <td className="py-3.5 px-5">
                      <div className="flex flex-wrap gap-1 max-w-[180px]">
                        {(client.produtos_divulgados || '').split(',').slice(0, 2).map((p, i) => (
                          <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[11px] font-medium rounded-md truncate max-w-[80px]">
                            {p.trim()}
                          </span>
                        ))}
                        {(client.produtos_divulgados || '').split(',').length > 2 && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-400 text-[11px] font-medium rounded-md">
                            +{(client.produtos_divulgados || '').split(',').length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3.5 px-5">
                      <div className="text-sm text-gray-600">
                        <span className="text-xs text-gray-400">Org:</span> {Number(client.comissao_organica || 0)}%
                        <span className="text-gray-300 mx-1">|</span>
                        <span className="text-xs text-gray-400">Trf:</span> {Number(client.comissao_trafego || 0)}%
                      </div>
                    </td>
                    <td className="py-3.5 px-5">
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <i className="ri-video-line text-xs text-brand-500"></i>
                          {Number(client.videos_30d || 0)}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-live-line text-xs text-rose-500"></i>
                          {Number(client.lives_30d || 0)}
                        </span>
                      </div>
                    </td>
                    <td className="py-3.5 px-5">
                      {client.status === 'active' ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                          Ativo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400">
                          <span className="w-1.5 h-1.5 bg-gray-300 rounded-full"></span>
                          Inativo
                        </span>
                      )}
                    </td>
                    <td className="py-3.5 px-5">
                      {client.amostra_enviada ? (
                        <div className="flex flex-col gap-1">
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-lg w-fit">
                            <i className="ri-check-double-line text-sm"></i>
                            Enviada
                          </span>
                          {client.amostra_data_envio && (
                            <span className="text-[10px] text-gray-400 pl-0.5">
                              {new Date(client.amostra_data_envio).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                            </span>
                          )}
                        </div>
                      ) : client.codigo_rastreio ? (
                        <div className="flex flex-col gap-1">
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600 bg-amber-50 px-2.5 py-1 rounded-lg w-fit">
                            <i className="ri-truck-line text-sm"></i>
                            Em trânsito
                          </span>
                          <span className="text-[10px] text-gray-400 font-mono pl-0.5 truncate max-w-[100px]" title={client.codigo_rastreio}>
                            {client.codigo_rastreio}
                          </span>
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 bg-gray-50 px-2.5 py-1 rounded-lg w-fit">
                          <i className="ri-close-circle-line text-sm"></i>
                          Não enviada
                        </span>
                      )}
                    </td>
                    {(canEdit || canDelete) && (
                      <td className="py-3.5 px-5">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {canEdit && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleSendForm(client); }}
                              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all cursor-pointer"
                              title="Enviar formulário via WhatsApp"
                            >
                              <i className="ri-send-plane-line text-sm"></i>
                            </button>
                          )}
                          {canEdit && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleEditClient(client); }}
                              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-all cursor-pointer"
                              title="Editar"
                            >
                              <i className="ri-edit-line text-sm"></i>
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setDeleteConfirm(client.id); }}
                              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all cursor-pointer"
                              title="Excluir"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredClients.length === 0 && (
          <div className="text-center py-16">
            <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <i className="ri-user-search-line text-2xl text-gray-300"></i>
            </div>
            <p className="text-sm text-gray-400">Nenhum creator encontrado</p>
          </div>
        )}
      </div>

      {/* Client Modal */}
      <ClientModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        client={selectedClient}
        onSave={handleSave}
      />

      {/* Detail Modal */}
      <ClientDetailModal
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        client={selectedClient}
      />

      {/* Send Form via WhatsApp Modal */}
      <SendFormWhatsAppModal
        isOpen={!!sendFormClient}
        onClose={() => setSendFormClient(null)}
        client={sendFormClient}
      />

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteConfirm(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 animate-[fadeIn_0.2s_ease-out]">
            <div className="w-12 h-12 bg-rose-50 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-2xl text-rose-500"></i>
            </div>
            <h3 className="text-base font-semibold text-gray-900 text-center mb-1">Excluir Creator</h3>
            <p className="text-sm text-gray-500 text-center mb-5">Tem certeza? Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDeleteClient(deleteConfirm)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-rose-600 rounded-lg hover:bg-rose-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-[fadeIn_0.2s_ease-out]">
          <div className="flex items-center gap-3 bg-gray-900 text-white px-5 py-3.5 rounded-xl shadow-2xl">
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${toast.type === 'success' ? 'bg-emerald-500/20' : 'bg-rose-500/20'}`}>
              <i className={`text-lg ${toast.type === 'success' ? 'ri-check-line text-emerald-400' : 'ri-close-line text-rose-400'}`}></i>
            </div>
            <p className="text-sm font-medium">{toast.message}</p>
          </div>
        </div>
      )}
    </div>
  );
}